// ==================== Domain Types ====================

export interface PhoneNumber {
  id: number;
  phone_number: string;
}

export interface Customer {
  id: number;
  phone_number?: PhoneNumber;
  addresses?: CustomerAddress[];
}

export interface CustomerAddress {
  id?: number;
  address?: string;
  floor?: string;
  name?: string;
  contact_phone?: string;
  extra?: string;
  customer?: Customer;
  last_used_at?: string;
  order_count?: number;
  lat?: number | null;
  lng?: number | null;
}

export interface Call {
  id: number;
  uuid: string;
  incoming_at: string;
  answered_at: string | null;
  finished_at: string | null;
  caller_number: string;
  caller_number_id?: number;
  agent: number | null;
  agent_count: number;
  recording_url: string | null;
  active: boolean;
  customer?: Customer | null;
  delivery: number[];
}

export interface CallQuality {
  quality_score: 'good' | 'fair' | 'poor' | 'critical' | 'unknown';
  packet_loss_pct: number;
  has_one_way_audio: number;
  has_high_jitter: number;
  has_packet_loss: number;
  rtp_in_quality_percentage: number;
  duration: number;
}

export interface MenuItem {
  id: number;
  sort_id: number;
  name: string;
  short_name?: string;
  description?: string;
  price: number;
  active: boolean;
  combo: boolean;
  option: boolean;
  visible_online: boolean;
  ui_color: number;
  deleted?: boolean;
  img_url?: string | null;
}

export interface MenuGroup {
  id: number;
  name: string;
  active: boolean;
  base: boolean;
  number_of_pieces: number;
  price_algorithm_max: boolean;
  description?: string;
  multi?: boolean;
  default_menu_item_id?: number | null;
  visible_online?: boolean;
  deleted?: boolean;
  img_url?: string | null;
}

export interface MenuItemGroupMember {
  menu_group_id: number;
  menu_item_id: number;
}

export interface MenuGroupGroupComplement {
  menu_group_id: number;
  complement_menu_group_id: number;
}

export interface MenuItemGroupComplement {
  menu_item_id: number;
  complement_menu_group_id: number;
}

export interface Location {
  id: number;
  name: string;
  address?: string;
  description?: string;
  requires_delivery?: boolean;
  active?: boolean;
  deleted?: boolean;
  lat?: number | null;
  lng?: number | null;
  is_default?: boolean;
}

export interface Agent {
  id: number;
  agent_name: string;
  agent_key: string;
}

export interface Deliveryman {
  id: number;
  name: string;
  vehicle?: string;
  barcode?: string;
  active?: boolean;
  deleted?: boolean;
}

export interface Order {
  id?: number;
  menu_item_id?: number;
  menu_item?: MenuItem;
  count: number;
  price?: number;
  gratis?: boolean;
  // Backend fields
  parent_order_item_id?: number | null;
  delivery_id?: number;
  note?: string | null;
  addons?: Order[];
  // Cart-specific fields
  uuid?: string;
  sibling_list?: string[];
  main_part?: boolean;
  total_price?: number;
  tainted?: boolean;
  group_id?: number;
  price_algorithm_max?: boolean;
  number_of_pieces?: number;
  combo?: boolean;
}

export interface Delivery {
  id: number;
  uuid: string;
  created_at: string;
  dispatched_at: string | null;
  canceled: boolean;
  canceled_at: string | null;
  canceled_reason?: string;
  amount?: number;
  printed: boolean;
  cc_payment?: boolean;
  online_paid?: boolean;
  // Online card payment lifecycle: pending | authorized | paid | failed | voided | refunded
  payment_status?: string | null;
  payment_intent_id?: string | null;
  online_order?: boolean;
  approved?: boolean | null;
  approved_at?: string | null;
  rejected_reason?: string;
  estimated_delivery_minutes?: number | null;
  discount?: number;
  delayed?: boolean;
  delayed_time?: string | null;
  note?: string;
  customer_address?: CustomerAddress;
  location?: Location;
  call?: Call;
  deliveryman?: number;
  orders: Order[];
}

export interface Checkin {
  location: number;
  deliveryman: number;
  time: string;
}

// ==================== Cart Types ====================

export interface CartItem {
  uuid: string;
  id: number;
  name: string;
  price: number;
  total_price: number;
  gratis: boolean;
  tainted?: boolean;
  combo?: boolean;
  main_part: boolean;
  group_id?: number;
  price_algorithm_max?: boolean;
  number_of_pieces?: number;
  addons?: CartItem[];
  sibling_list?: string[];
  sort_id?: number;
  description?: string;
  active?: boolean;
  option?: boolean;
  visible_online?: boolean;
  ui_color?: number;
}

export interface CartState {
  cartItems: Record<string, CartItem>;
  selectedItem: string;
  partialItems: {
    cartItems: Record<string, CartItem>;
    group_key: number | null;
    price_algo_max: boolean;
    number_of_pieces: number | null;
  };
}

// ==================== UI Types ====================

export interface UIVisibleComponents {
  ui_delay: boolean;
  ui_gratis: boolean;
  ui_nth_gratis: boolean;
  ui_loyalty: boolean;
  ui_mini_stats: boolean;
  ui_distance: boolean;
  ui_discount: boolean;
  ui_payment_opts: boolean;
  ui_map_overlay: boolean;
  ui_call_stats: boolean;
  ui_tablet_view: boolean;
}

export interface DeliveryAddressDistance {
  distances: Record<number, number>;
}

export interface FeedbackMessage {
  id: string;
  message: string;
  severity: MessageSeverity;
  timeout: number;
  autoRemove?: boolean;
  sound?: string;
}

export enum MessageSeverity {
  SUCCESS = 'SUCCESS',
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
}

export enum PaymentType {
  CASH = 'cache', // kept as 'cache' for backend compatibility
  CC = 'cc',
}

// ==================== Stats Types ====================

export interface LocationBalanceStat {
  id: number;
  name: string;
  scanned: number;
  not_scanned: number;
  total: number;
  total_cc: number;
  total_online: number;
  delivery_count: number;
}

export interface LocationBalanceStats {
  stats: LocationBalanceStat[];
  from_date: string;
  to_date: string;
}

export interface DeliveryManAddress {
  delivery_id: number;
  address: string;
  cc_payment: boolean;
  online_paid: boolean;
  amount: number;
  canceled: boolean;
}

export interface DeliveryManLocation {
  location_id: number;
  location: string;
  addresses: DeliveryManAddress[];
}

export interface DeliveryManStat {
  id: number;
  name: string;
  locations: DeliveryManLocation[];
  total: number;
  total_cc: number;
  total_online: number;
  points: number;
}

export interface DeliveryMenStats {
  stats: DeliveryManStat[];
  from_date: string;
  to_date: string;
}

export interface ItemSoldStat {
  menu_item_id: number;
  count: number;
  gratis: boolean;
}

export interface ItemsSoldStats {
  stats: ItemSoldStat[];
  from_date: string;
  to_date: string;
}

// ==================== WebSocket Response ====================

export interface WsResponse<T = unknown> {
  status: {
    code: number;
    message: string;
  };
  payload?: T;
}

// ==================== Trends Types ====================

export interface TrendsRevenueVolume {
  period: string;
  revenue: number;
  order_count: number;
}

export interface TrendsPhoneVsOnline {
  period: string;
  phone_count: number;
  phone_revenue: number;
  online_count: number;
  online_revenue: number;
}

export interface TrendsPaymentMethod {
  period: string;
  cash_count: number;
  cash_revenue: number;
  cc_count: number;
  cc_revenue: number;
  online_paid_count?: number;
  online_paid_revenue?: number;
}

export interface TrendsDiscount {
  period: string;
  discount_total: number;
  discount_count: number;
  gratis_count: number;
}

export interface TrendsRevenueKPI {
  total_revenue: number;
  order_count: number;
  avg_order: number;
}

export interface TrendsRevenueResponse {
  revenue_volume: TrendsRevenueVolume[];
  phone_vs_online: TrendsPhoneVsOnline[];
  payment_methods: TrendsPaymentMethod[];
  discounts: TrendsDiscount[];
  kpi: TrendsRevenueKPI;
}

export interface TrendsHeatmapEntry {
  day: number;
  hour: number;
  count: number;
}

export interface TrendsHourlyEntry {
  hour: number;
  count: number;
}

export interface TrendsDeliverySpeed {
  period: string;
  avg_bake_minutes: number;
  avg_dispatch_minutes: number;
}

export interface TrendsCancellationRate {
  period: string;
  total: number;
  canceled: number;
  rate_pct: number;
}

export interface TrendsCancellationReason {
  reason: string;
  count: number;
}

export interface TrendsRejectionRate {
  period: string;
  online_total: number;
  rejected: number;
  refunded: number;
}

export interface TrendsRejectionsKPI {
  online_total: number;
  rejected: number;
  refunded: number;
}

export interface TrendsOperationsResponse {
  peak_hours: {
    heatmap: TrendsHeatmapEntry[];
    hourly: TrendsHourlyEntry[];
  };
  delivery_speed: TrendsDeliverySpeed[];
  cancellations: {
    rate: TrendsCancellationRate[];
    reasons: TrendsCancellationReason[];
  };
  rejections?: {
    rate: TrendsRejectionRate[];
    reasons: TrendsCancellationReason[];
    kpi: TrendsRejectionsKPI;
  };
}

export interface TrendsItemByQuantity {
  menu_item_id: number;
  name: string;
  count: number;
}

export interface TrendsItemByRevenue {
  menu_item_id: number;
  name: string;
  revenue: number;
}

export interface TrendsItemByCategory {
  group_id: number;
  group_name: string;
  count: number;
}

export interface TrendsItemsResponse {
  by_quantity: TrendsItemByQuantity[];
  by_revenue: TrendsItemByRevenue[];
  by_category: TrendsItemByCategory[];
}

export interface TrendsCallVolume {
  period: string;
  total_calls: number;
  answered_calls: number;
}

export interface TrendsCallConversion {
  period: string;
  calls_with_order: number;
  total_calls: number;
  rate_pct: number;
}

export interface TrendsCallsResponse {
  volume: TrendsCallVolume[];
  conversion: TrendsCallConversion[];
  kpi_conversion_pct: number;
}

export interface TrendsNewVsReturning {
  period: string;
  /** first-ever orders — phone number never seen before (all-time) */
  new_customers: number;
  /** orders placed by customers with at least one earlier order */
  returning_orders: number;
}

export interface TrendsTopCustomer {
  customer_id: number;
  phone: string;
  name: string;
  order_count: number;
  total_revenue: number;
}

export interface TrendsCustomersResponse {
  new_vs_returning: TrendsNewVsReturning[];
  top_customers: TrendsTopCustomer[];
}

export interface TrendsDriver {
  deliveryman_id: number;
  name: string;
  delivery_count: number;
  revenue: number;
}

export interface TrendsAgent {
  agent_id: number;
  agent_name: string;
  order_count: number;
  revenue: number;
}

export interface TrendsPersonnelResponse {
  drivers: TrendsDriver[];
  agents: TrendsAgent[];
}

// ==================== Auth Types ====================

export interface JwtPayload {
  public_id: string;
  email: string;
  admin: boolean;
  exp: number;
}
